import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyTimelineEvent = () => ({
  time: '',
  action: '',
  equipment_involved: '',
  who_was_present: '',
  notes: '',
});

const initialForm = {
  incident_description: '',
  meeting_type: 'fact finder',
  user_role: '',
  task_being_performed: '',
  representative_mentioned: null,
  location: '',
  incident_date: '',
  who_was_present: '',
  instructions_given: '',
  actions_taken: '',
  equipment_involved: '',
  what_happened_next: '',
  additional_details: '',
  timeline: [emptyTimelineEvent()],
};

function FactFinderPage({ user }) {
  const navigate = useNavigate();
  
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('preparation');
  const [copyStatus, setCopyStatus] = useState('idle'); // idle, copied, failed

  const canSubmit = useMemo(() => {
    return form.incident_description.trim().length >= 10;
  }, [form.incident_description]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateTimeline = (index, field, value) => {
    const updated = [...form.timeline];
    updated[index] = { ...updated[index], [field]: value };
    setForm((prev) => ({ ...prev, timeline: updated }));
  };

  const addTimelineEvent = () => {
    setForm((prev) => ({
      ...prev,
      timeline: [...prev.timeline, emptyTimelineEvent()],
    }));
  };

  const removeTimelineEvent = (index) => {
    setForm((prev) => ({
      ...prev,
      timeline: prev.timeline.filter((_, i) => i !== index),
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setResult(null);
    setError('');
    setActiveTab('preparation');
    setCopyStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError('');
    setResult(null);
    setCopyStatus('idle');

    try {
      // Clean timeline - only include events with actions
      const cleanedTimeline = form.timeline
        .map(item => ({
          ...item,
          time: item.time?.trim() || undefined,
          action: item.action.trim(),
          equipment_involved: item.equipment_involved?.trim() || undefined,
          who_was_present: item.who_was_present?.trim() || undefined,
          notes: item.notes?.trim() || undefined,
        }))
        .filter(item => item.action);

      const payload = {
        ...form,
        incident_description: form.incident_description.trim(),
        meeting_type: form.meeting_type?.trim() || undefined,
        user_role: form.user_role?.trim() || undefined,
        task_being_performed: form.task_being_performed?.trim() || undefined,
        location: form.location?.trim() || undefined,
        incident_date: form.incident_date?.trim() || undefined,
        who_was_present: form.who_was_present?.trim() || undefined,
        instructions_given: form.instructions_given?.trim() || undefined,
        actions_taken: form.actions_taken?.trim() || undefined,
        equipment_involved: form.equipment_involved?.trim() || undefined,
        what_happened_next: form.what_happened_next?.trim() || undefined,
        additional_details: form.additional_details?.trim() || undefined,
        timeline: cleanedTimeline,
      };
      
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/fact-finder/prepare`,
        payload,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to prepare guidance');
    } finally {
      setLoading(false);
    }
  };

  const copyStatement = async () => {
    if (!result?.statement_builder?.statement_draft) return;

    try {
      await navigator.clipboard.writeText(result.statement_builder.statement_draft);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 3000);
    } catch {
      setCopyStatus('failed');
    }
  };

  return (
    <div className="as-analytics-page" data-testid="fact-finder-page">
      <header className="as-header">
        <div className="as-badge">Meeting Prep + Statement</div>
        <h1>Fact Finder & Statement Builder</h1>
        <p>Organize incident details, build a timeline, prepare for the meeting, and generate a neutral statement draft.</p>
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
      </header>

      <div className="as-card">
        <div className="card-header">
          <h2>Incident Details</h2>
          <p>Provide information about the incident and upcoming meeting</p>
        </div>
        <div className="card-body">
          {result ? (
            <div className="result-panel" data-testid="fact-finder-result">
              <div className="result-header">
                <h3>Guidance & Statement</h3>
                <span className="badge">{result.incident_type}</span>
              </div>

              {/* Tab Navigation */}
              <div className="result-tabs">
                <button
                  className={`tab-btn ${activeTab === 'preparation' ? 'active' : ''}`}
                  onClick={() => setActiveTab('preparation')}
                >
                  Meeting Preparation
                </button>
                <button
                  className={`tab-btn ${activeTab === 'statement' ? 'active' : ''}`}
                  onClick={() => setActiveTab('statement')}
                >
                  Statement Draft
                </button>
                <button
                  className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
                  onClick={() => setActiveTab('summary')}
                >
                  Fact Summary
                </button>
                <button
                  className={`tab-btn ${activeTab === 'references' ? 'active' : ''}`}
                  onClick={() => setActiveTab('references')}
                >
                  References
                </button>
              </div>

              {/* Preparation Tab */}
              {activeTab === 'preparation' && (
                <div className="tab-content">
                  <div className="result-section">
                    <h4>Possible Topics</h4>
                    <div className="tag-list">
                      {result.possible_topics.map((topic, idx) => (
                        <span key={idx} className="tag">{topic}</span>
                      ))}
                    </div>
                  </div>

                  <div className="result-section">
                    <h4>What This Meeting May Involve</h4>
                    <p>{result.what_this_meeting_may_involve}</p>
                  </div>

                  <div className="result-section">
                    <h4>Possible Next Steps</h4>
                    <ul className="checklist">
                      {result.possible_next_steps.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="result-section">
                    <h4>Preparation Checklist</h4>
                    <ul className="checklist">
                      {result.prep_checklist.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="result-section">
                    <h4>Suggested Questions to Ask</h4>
                    <ul className="checklist">
                      {result.suggested_questions.map((q, idx) => (
                        <li key={idx}>{q}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="result-section">
                    <h4>Areas to Review</h4>
                    <ul className="checklist">
                      {result.areas_to_review.map((area, idx) => (
                        <li key={idx}>{area}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Statement Tab */}
              {activeTab === 'statement' && (
                <div className="tab-content">
                  <div className="result-section">
                    <div className="section-header-row">
                      <h4>Your Statement Draft</h4>
                      <div className="copy-btn-group">
                        <button className="btn-secondary small" onClick={copyStatement}>
                          📋 Copy
                        </button>
                        {copyStatus === 'copied' && <span className="copy-status success">Copied!</span>}
                        {copyStatus === 'failed' && <span className="copy-status error">Failed</span>}
                      </div>
                    </div>
                    <pre className="fact-summary statement-draft">{result.statement_builder.statement_draft}</pre>
                  </div>

                  {result.statement_builder.missing_information.length > 0 && (
                    <div className="result-section">
                      <h4>Missing Information</h4>
                      <p className="section-note">Consider adding these details to strengthen your statement:</p>
                      <ul className="missing-list">
                        {result.statement_builder.missing_information.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.statement_builder.clarifying_questions.length > 0 && (
                    <div className="result-section">
                      <h4>Clarifying Questions</h4>
                      <p className="section-note">Think about these questions to add more detail:</p>
                      <ul className="checklist">
                        {result.statement_builder.clarifying_questions.map((q, idx) => (
                          <li key={idx}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="result-section">
                    <h4>Tone Suggestions</h4>
                    <ul className="checklist">
                      {result.statement_builder.tone_suggestions.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <div className="tab-content">
                  <div className="result-section">
                    <h4>Fact Summary</h4>
                    <pre className="fact-summary">{result.fact_summary}</pre>
                  </div>
                </div>
              )}

              {/* References Tab */}
              {activeTab === 'references' && (
                <div className="tab-content">
                  <div className="result-section">
                    <h4>Contract / Policy References to Check</h4>
                    <p className="section-note">Review these sections that may be relevant to your situation:</p>
                    <ul className="checklist">
                      {result.contract_references.map((ref, idx) => (
                        <li key={idx}>{ref}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="disclaimer-box">
                <strong>Disclaimer:</strong> {result.disclaimer}
              </div>

              <div className="button-row">
                <button className="btn-primary" onClick={resetForm}>Start Over</button>
                <button className="btn-secondary" onClick={() => window.print()}>Print</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Incident Overview Section */}
              <div className="form-section-header">Incident Overview</div>
              
              <div className="field-group">
                <label>Incident Description *</label>
                <textarea
                  value={form.incident_description}
                  onChange={(e) => updateField('incident_description', e.target.value)}
                  placeholder="Describe what happened. Example: Supervisor told me there will be a fact finder tomorrow about a switching error during breaker operations."
                  data-testid="incident-description"
                />
                {form.incident_description.length > 0 && form.incident_description.length < 10 && (
                  <span className="field-hint error">Minimum 10 characters required</span>
                )}
              </div>

              <div className="field-row">
                <div className="field-group half">
                  <label>Incident Date</label>
                  <input
                    type="text"
                    value={form.incident_date}
                    onChange={(e) => updateField('incident_date', e.target.value)}
                    placeholder="Example: March 8, 2026"
                    className="text-input"
                    data-testid="incident-date"
                  />
                </div>
                <div className="field-group half">
                  <label>Meeting Type</label>
                  <input
                    type="text"
                    value={form.meeting_type}
                    onChange={(e) => updateField('meeting_type', e.target.value)}
                    placeholder="Fact finder, review meeting..."
                    className="text-input"
                    data-testid="meeting-type"
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group half">
                  <label>Your Role</label>
                  <select
                    value={form.user_role}
                    onChange={(e) => updateField('user_role', e.target.value)}
                    className="select-input"
                    data-testid="user-role"
                  >
                    <option value="">Select...</option>
                    <option value="involved">Directly involved</option>
                    <option value="witness">Witness</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="crew member">Crew member</option>
                  </select>
                </div>
                <div className="field-group half">
                  <label>Was a representative mentioned?</label>
                  <select
                    value={form.representative_mentioned === null ? '' : String(form.representative_mentioned)}
                    onChange={(e) =>
                      updateField(
                        'representative_mentioned',
                        e.target.value === '' ? null : e.target.value === 'true'
                      )
                    }
                    className="select-input"
                    data-testid="representative-mentioned"
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              {/* Work Details Section */}
              <div className="form-section-header">Work Details</div>

              <div className="field-row">
                <div className="field-group half">
                  <label>Task Being Performed</label>
                  <input
                    type="text"
                    value={form.task_being_performed}
                    onChange={(e) => updateField('task_being_performed', e.target.value)}
                    placeholder="Switching, breaker replacement..."
                    className="text-input"
                    data-testid="task-performed"
                  />
                </div>
                <div className="field-group half">
                  <label>Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="Substation, yard, job site..."
                    className="text-input"
                    data-testid="location"
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group half">
                  <label>Who Was Present?</label>
                  <input
                    type="text"
                    value={form.who_was_present}
                    onChange={(e) => updateField('who_was_present', e.target.value)}
                    placeholder="Crew leader, operator..."
                    className="text-input"
                    data-testid="who-present"
                  />
                </div>
                <div className="field-group half">
                  <label>Equipment Involved</label>
                  <input
                    type="text"
                    value={form.equipment_involved}
                    onChange={(e) => updateField('equipment_involved', e.target.value)}
                    placeholder="Breaker 12A, relay panel..."
                    className="text-input"
                    data-testid="equipment-involved"
                  />
                </div>
              </div>

              {/* Instructions and Actions Section */}
              <div className="form-section-header">Instructions and Actions</div>

              <div className="field-group">
                <label>Instructions Given</label>
                <textarea
                  value={form.instructions_given}
                  onChange={(e) => updateField('instructions_given', e.target.value)}
                  placeholder="What instructions did you understand at the time?"
                  data-testid="instructions-given"
                />
              </div>

              <div className="field-group">
                <label>Actions You Took</label>
                <textarea
                  value={form.actions_taken}
                  onChange={(e) => updateField('actions_taken', e.target.value)}
                  placeholder="What actions did you take?"
                  data-testid="actions-taken"
                />
              </div>

              <div className="field-group">
                <label>What Happened Next?</label>
                <textarea
                  value={form.what_happened_next}
                  onChange={(e) => updateField('what_happened_next', e.target.value)}
                  placeholder="What happened immediately after the event?"
                  data-testid="what-happened-next"
                />
              </div>

              <div className="field-group">
                <label>Additional Details</label>
                <textarea
                  value={form.additional_details}
                  onChange={(e) => updateField('additional_details', e.target.value)}
                  placeholder="Anything else relevant?"
                  data-testid="additional-details"
                />
              </div>

              {/* Timeline Builder Section */}
              <div className="timeline-section">
                <div className="timeline-header">
                  <h3>Timeline Builder</h3>
                  <button type="button" className="btn-secondary" onClick={addTimelineEvent}>
                    + Add Event
                  </button>
                </div>

                {form.timeline.map((event, index) => (
                  <div key={index} className="timeline-event">
                    <div className="event-header">
                      <span className="event-number">Event {index + 1}</span>
                      {form.timeline.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => removeTimelineEvent(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="field-row">
                      <div className="field-group half">
                        <input
                          type="text"
                          value={event.time}
                          onChange={(e) => updateTimeline(index, 'time', e.target.value)}
                          placeholder="Time (e.g., 08:15)"
                          className="text-input"
                        />
                      </div>
                      <div className="field-group half">
                        <input
                          type="text"
                          value={event.action}
                          onChange={(e) => updateTimeline(index, 'action', e.target.value)}
                          placeholder="Action taken *"
                          className="text-input"
                        />
                      </div>
                    </div>
                    <div className="field-row">
                      <div className="field-group half">
                        <input
                          type="text"
                          value={event.equipment_involved}
                          onChange={(e) => updateTimeline(index, 'equipment_involved', e.target.value)}
                          placeholder="Equipment involved"
                          className="text-input"
                        />
                      </div>
                      <div className="field-group half">
                        <input
                          type="text"
                          value={event.who_was_present}
                          onChange={(e) => updateTimeline(index, 'who_was_present', e.target.value)}
                          placeholder="Who was present"
                          className="text-input"
                        />
                      </div>
                    </div>
                    <div className="field-group">
                      <input
                        type="text"
                        value={event.notes}
                        onChange={(e) => updateTimeline(index, 'notes', e.target.value)}
                        placeholder="Notes"
                        className="text-input"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {error && <div className="error-message" data-testid="error">{error}</div>}

              <div className="button-row">
                <button 
                  type="submit"
                  className="btn-primary" 
                  disabled={loading || !canSubmit}
                  data-testid="prepare-btn"
                >
                  {loading ? 'Preparing...' : 'Prepare Guidance + Build Statement'}
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>Reset</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default FactFinderPage;
